/** Local-date helpers. Every date in Kaya is a local `YYYY-MM-DD` string, never a UTC instant —
 *  a session done at 11pm belongs to that day, not the next one. */

export const DAY_MS = 864e5;

export function todayStr(d?: Date): string {
  const x = d || new Date();
  return (
    x.getFullYear() +
    "-" +
    String(x.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(x.getDate()).padStart(2, "0")
  );
}

/** Parses a `YYYY-MM-DD` string at local midnight. */
export function parseDay(ds: string): Date {
  return new Date(ds + "T00:00:00");
}

export function addDays(ds: string, n: number): string {
  const d = parseDay(ds);
  d.setDate(d.getDate() + n);
  return todayStr(d);
}

export function daysBetween(a: string, b: string): number {
  return Math.round((parseDay(b).getTime() - parseDay(a).getTime()) / DAY_MS);
}

/** Monday of the week containing `d`, as a date string. Weeks run Mon–Sun. */
export function weekKey(d?: Date): string {
  const x = d || new Date();
  const m = new Date(x);
  m.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return todayStr(m);
}

/** The seven date strings of the week containing `d`, Monday first. */
export function weekDays(d?: Date): string[] {
  const mon = parseDay(weekKey(d));
  const out: string[] = [];
  for (let i = 0; i < 7; i++) {
    const x = new Date(mon);
    x.setDate(mon.getDate() + i);
    out.push(todayStr(x));
  }
  return out;
}

export function fmtClock(ms: number): string {
  const s = Math.ceil(ms / 1000);
  return Math.floor(s / 60) + ":" + String(s % 60).padStart(2, "0");
}

/** "07:42" — a clock time in 24-hour form, whatever the phone's locale. Takes epoch ms or an ISO
 *  string, since food entries stamp the one and walks the other. */
export function clockTime(t: number | string): string {
  const d = new Date(t);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Wed 23 Sep" — the one date style (audit §6), built by hand so the locale cannot reorder it. */
export function dayLabel(ds: string): string {
  const d = parseDay(ds);
  return `${DOW_LABELS[d.getDay()]} ${d.getDate()} ${MONTH_LABELS[d.getMonth()]}`;
}

/** "3 Oct" — where the weekday would only crowd a sentence. */
export function dayMonth(ds: string): string {
  const d = parseDay(ds);
  return `${d.getDate()} ${MONTH_LABELS[d.getMonth()]}`;
}
